/* ENRAGED — core storefront script.
   Custom elements (safe across theme-editor section reloads) + one set of document-level listeners. */
(() => {
  if (window.__enragedCore) return;
  window.__enragedCore = true;

  const theme = (window.theme = window.theme || {});
  const routes = theme.routes || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------- Utilities ---------- */
  theme.debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  theme.formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision);
      const [whole, frac] = fixed.split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0);
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
        default: return fmt(value, 2);
      }
    });
  };

  theme.announce = (message) => {
    const region = document.getElementById('A11yStatus');
    if (!region) return;
    region.textContent = '';
    setTimeout(() => { region.textContent = message; }, 60);
  };

  /* Shipping protection is an add-on line, not a product the shopper picked; keep it out of the count. */
  theme.realCount = (cart) => {
    const protection = (cart.items || []).filter((item) => item.variant_id === theme.protectionVariantId);
    return cart.item_count - protection.reduce((sum, item) => sum + item.quantity, 0);
  };

  theme.updateCartCount = (count) => {
    if (count === undefined || count === null) return;
    const n = Number(count) || 0;
    document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = n > 0 ? String(n) : ''; });
    document.querySelectorAll('[data-cart-count-label]').forEach((el) => {
      const tpl = n === 1 ? el.dataset.one : el.dataset.other;
      if (tpl) el.textContent = tpl.replace('[count]', n);
    });
  };

  theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  theme.postJSON = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || theme.strings?.cartError || 'Error');
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  };

  /* ---------- Dialogs (menu drawer, search, cart drawer) ---------- */
  const openers = new WeakMap();

  theme.openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    openers.set(dialog, opener || document.activeElement);
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    const focusTarget = dialog.querySelector('[autofocus]') || dialog.querySelector('[data-initial-focus]');
    if (focusTarget) requestAnimationFrame(() => focusTarget.focus());
    document.querySelectorAll('[aria-controls="' + dialog.id + '"]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
  };

  theme.closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    document.querySelectorAll('[aria-controls="' + dialog.id + '"]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    const opener = openers.get(dialog);
    if (opener && typeof opener.focus === 'function' && document.contains(opener)) opener.focus({ preventScroll: true });
    openers.delete(dialog);
  }, true);

  /* ---------- Header navigation (details/summary) ---------- */
  const closeNavDetails = (except) => {
    document.querySelectorAll('[data-nav-details][open]').forEach((d) => { if (d !== except) d.removeAttribute('open'); });
  };

  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (details.matches && details.matches('[data-nav-details]') && details.open) closeNavDetails(details);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('[data-nav-details][open]');
    if (open) {
      open.removeAttribute('open');
      open.querySelector('summary')?.focus();
    }
  });

  document.addEventListener('focusout', (event) => {
    const details = event.target.closest && event.target.closest('[data-nav-details][open]');
    if (details && event.relatedTarget && !details.contains(event.relatedTarget)) details.removeAttribute('open');
  });

  /* ---------- Delegated clicks ---------- */
  document.addEventListener('click', (event) => {
    const target = event.target;

    if (!target.closest('[data-nav-details]')) closeNavDetails();

    const opener = target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog) {
        event.preventDefault();
        theme.openDialog(dialog, opener);
      }
      return;
    }

    const closer = target.closest('[data-close-dialog]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) {
        event.preventDefault();
        theme.closeDialog(dialog);
      }
      return;
    }

    if (target instanceof HTMLDialogElement && target.open) {
      const rect = target.getBoundingClientRect();
      const outside = event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
      if (outside) theme.closeDialog(target);
      return;
    }

    const cartToggle = target.closest('[data-cart-toggle]');
    if (cartToggle && theme.cartType === 'drawer') {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(cartToggle);
      }
    }
  });

  /* ---------- Sticky header offset ---------- */
  const setHeaderOffset = () => {
    const header = document.querySelector('.section-header');
    if (!header) return;
    const h = Math.round(header.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--header-offset', h + 'px');
  };
  window.addEventListener('resize', theme.debounce(setHeaderOffset, 150));
  document.addEventListener('DOMContentLoaded', setHeaderOffset);
  document.addEventListener('shopify:section:load', setHeaderOffset);

  /* ---------- Cart items (drawer + cart page) ---------- */
  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onChange = this.onChange.bind(this);
      this.onClick = this.onClick.bind(this);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      this.pending = new Map();
      this.removeOrphanProtection();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
    }

    get sectionId() { return this.dataset.sectionId; }
    get rootSelector() { return this.dataset.root; }

    sectionsToRender() {
      const ids = new Set();
      document.querySelectorAll('cart-items[data-section-id]').forEach((el) => ids.add(el.dataset.sectionId));
      return [...ids];
    }

    onClick(event) {
      const step = event.target.closest('[data-qty-step]');
      if (step) {
        const input = step.closest('.qty')?.querySelector('input');
        if (!input) return;
        const next = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyStep));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const remove = event.target.closest('[data-line-remove]');
      if (remove) {
        event.preventDefault();
        this.updateLine(remove.dataset.line, 0, remove);
        return;
      }
      const add = event.target.closest('[data-cart-add]');
      if (add) {
        event.preventDefault();
        this.addVariant(add.dataset.cartAdd, add);
      }
    }

    onChange(event) {
      const input = event.target;
      if (input.matches('[data-line-qty]')) {
        const line = input.dataset.line;
        clearTimeout(this.pending.get(line));
        this.pending.set(line, setTimeout(() => this.updateLine(line, parseInt(input.value, 10) || 0, input), 350));
      } else if (input.matches('[data-protection-toggle]')) {
        this.toggleProtection(input);
      } else if (input.matches('[data-cart-note]')) {
        theme.postJSON(routes.cartUpdate + '.js', { note: input.value }).catch(() => {});
      }
    }

    setBusy(line, busy) {
      const el = this.querySelector('[data-cart-line="' + line + '"]');
      if (el) el.classList.toggle('is-loading', busy);
    }

    showError(line, message) {
      const el = this.querySelector('[data-line-error="' + line + '"]') || this.querySelector('[data-cart-error]');
      if (el) {
        el.textContent = message;
        el.hidden = false;
      }
      theme.announce(message);
    }

    async updateLine(line, quantity, control) {
      this.setBusy(line, true);
      try {
        const state = await theme.postJSON(routes.cartChange + '.js', {
          line: Number(line), quantity, sections: this.sectionsToRender(), sections_url: window.location.pathname,
        });
        theme.renderCartSections(state, control);
      } catch (error) {
        this.setBusy(line, false);
        this.showError(line, error.message);
        theme.refreshCart();
      }
    }

    async addVariant(variantId, control) {
      if (control) control.classList.add('is-loading');
      try {
        const state = await theme.postJSON(routes.cartAdd + '.js', {
          items: [{ id: Number(variantId), quantity: 1 }], sections: this.sectionsToRender(), sections_url: window.location.pathname,
        });
        theme.renderCartSections(state, control);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'cart-upsell' } }));
      } catch (error) {
        if (control) control.classList.remove('is-loading');
        this.showError('upsell', error.message);
      }
    }

    async toggleProtection(input) {
      input.disabled = true;
      try {
        let state;
        if (input.checked) {
          state = await theme.postJSON(routes.cartAdd + '.js', {
            items: [{ id: Number(input.dataset.variantId), quantity: 1 }], sections: this.sectionsToRender(), sections_url: window.location.pathname,
          });
        } else {
          state = await theme.postJSON(routes.cartChange + '.js', {
            id: input.dataset.lineKey, quantity: 0, sections: this.sectionsToRender(), sections_url: window.location.pathname,
          });
        }
        theme.renderCartSections(state, input);
      } catch (error) {
        input.checked = !input.checked;
        input.disabled = false;
        this.showError('protection', error.message);
      }
    }

    removeOrphanProtection() {
      const root = this.rootSelector ? this.querySelector(this.rootSelector) || this : this;
      const holder = root.querySelector('[data-real-count]') || (root.dataset && root.dataset.realCount !== undefined ? root : null);
      if (!holder) return;
      if (holder.dataset.realCount === '0' && holder.dataset.protectionKey) {
        theme.postJSON(routes.cartChange + '.js', {
          id: holder.dataset.protectionKey, quantity: 0, sections: this.sectionsToRender(), sections_url: window.location.pathname,
        }).then((state) => theme.renderCartSections(state)).catch(() => {});
      }
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  theme.renderCartSections = (state, control) => {
    if (!state) return;
    const focusId = control && control.id;
    const sections = state.sections || {};
    document.querySelectorAll('cart-items[data-section-id]').forEach((el) => {
      const html = sections[el.dataset.sectionId];
      if (!html) return;
      const doc = theme.parseHTML(html);
      const selector = el.dataset.root;
      const fresh = doc.querySelector(selector);
      const current = document.querySelector(selector);
      if (fresh && current) current.replaceWith(fresh);
    });
    const countSource = Object.values(sections).map((html) => html && theme.parseHTML(html).querySelector('[data-cart-item-count]')).find(Boolean);
    if (countSource) theme.updateCartCount(countSource.dataset.cartItemCount);
    else if (state.item_count !== undefined) theme.updateCartCount(theme.realCount(state));
    if (focusId) {
      const next = document.getElementById(focusId) || document.querySelector('[data-cart-focus-fallback]');
      if (next) next.focus({ preventScroll: true });
    }
    document.querySelectorAll('cart-items').forEach((el) => el.removeOrphanProtection && el.removeOrphanProtection());
    document.dispatchEvent(new CustomEvent('cart:rendered'));
  };

  theme.refreshCart = async () => {
    const ids = [...new Set([...document.querySelectorAll('cart-items[data-section-id]')].map((el) => el.dataset.sectionId))];
    if (!ids.length) {
      const cart = await fetch(routes.cart + '.js').then((r) => r.json()).catch(() => null);
      if (cart) theme.updateCartCount(theme.realCount(cart));
      return;
    }
    const sections = await fetch(routes.root + '?sections=' + ids.join(',')).then((r) => r.json()).catch(() => null);
    if (sections) theme.renderCartSections({ sections });
  };

  /* ---------- Cart drawer (also the Atlas bundler integration point) ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.lastRender = 0;
    }

    open(opener) {
      if (!this.dialog) return;
      theme.openDialog(this.dialog, opener);
    }

    close() { theme.closeDialog(this.dialog); }

    /* Contract used by the Atlas bundler app block: it requests these sections with its /cart/add.js call
       and hands the response to renderContents(). */
    getSectionsToRender() {
      return [{ id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer-Inner' }];
    }

    setActiveElement(element) { this.activeElement = element; }

    async renderContents(parsedState) {
      this.lastRender = Date.now();
      const html = parsedState && parsedState.sections && parsedState.sections['cart-drawer'];
      if (html) theme.renderCartSections(parsedState);
      else await theme.refreshCart();
      this.open(this.activeElement);
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  /* ---------- Quick add (cards / spotlights outside the product page) ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-quick-add]');
    if (!form || theme.cartType !== 'drawer') return;
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-form-error]');
    if (error) error.hidden = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    const body = new FormData(form);
    body.append('sections', drawer.getSectionsToRender().map((s) => s.section).join(','));
    body.append('sections_url', window.location.pathname);
    try {
      const response = await fetch(routes.cartAdd + '.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || theme.strings.cartError);
      drawer.setActiveElement(button);
      await drawer.renderContents(data);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'quick-add', __enraged: true } }));
    } catch (err) {
      if (error) {
        error.textContent = err.message;
        error.hidden = false;
      }
      theme.announce(err.message);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  document.addEventListener('cart:refresh', () => theme.refreshCart());
  document.addEventListener('cart:add', (event) => {
    if (event.detail && event.detail.__enraged) return;
    setTimeout(() => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && Date.now() - drawer.lastRender < 1500) return;
      theme.refreshCart();
    }, 600);
  });

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.onInput = theme.debounce(this.search.bind(this), 260);
      this.onKey = this.onKeydown.bind(this);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('keydown', this.onKey);
      this.cache = new Map();
    }

    disconnectedCallback() {
      if (!this.input) return;
      this.input.removeEventListener('input', this.onInput);
      this.input.removeEventListener('keydown', this.onKey);
      if (this.controller) this.controller.abort();
    }

    async search() {
      const term = this.input.value.trim();
      if (!term) {
        this.results.innerHTML = '';
        this.input.setAttribute('aria-expanded', 'false');
        return;
      }
      if (this.cache.has(term)) return this.render(this.cache.get(term));
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({ q: term, section_id: 'predictive-search', 'resources[limit]': '6', 'resources[limit_scope]': 'each' });
      try {
        const res = await fetch(routes.predictiveSearch + '?' + params.toString(), { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const markup = theme.parseHTML(html).querySelector('#shopify-section-predictive-search')?.innerHTML || '';
        this.cache.set(term, markup);
        this.render(markup);
      } catch (error) {
        if (error.name !== 'AbortError') this.results.innerHTML = '';
      }
    }

    render(markup) {
      this.results.innerHTML = markup;
      this.input.setAttribute('aria-expanded', markup ? 'true' : 'false');
      this.activeIndex = -1;
      const count = this.results.querySelector('[data-predictive-count]');
      if (count) theme.announce(count.textContent.trim());
    }

    onKeydown(event) {
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const dir = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + dir + options.length) % options.length;
        options.forEach((o, i) => o.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false'));
        const active = options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        const link = options[this.activeIndex].querySelector('a');
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Video ---------- */
  class EVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.autoplay = this.hasAttribute('data-autoplay') && !reducedMotion.matches;
      this.playBtn = this.querySelector('[data-video-toggle]');
      this.muteBtn = this.querySelector('[data-video-mute]');
      this.bigPlay = this.querySelector('[data-video-play]');
      this.onToggle = () => (this.video.paused ? this.play(true) : this.pause(true));
      this.onMute = () => this.setMuted(!this.video.muted);
      this.onBigPlay = () => {
        this.video.controls = this.hasAttribute('data-controls-on-play');
        if (this.video.controls) this.setMuted(false);
        this.play(true);
      };
      this.playBtn?.addEventListener('click', this.onToggle);
      this.muteBtn?.addEventListener('click', this.onMute);
      this.bigPlay?.addEventListener('click', this.onBigPlay);
      this.video.addEventListener('play', () => this.sync());
      this.video.addEventListener('pause', () => this.sync());
      if (this.autoplay) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.userPaused) this.play(false);
            else if (!entry.isIntersecting && !this.video.paused) this.pause(false);
          });
        }, { rootMargin: '120px 0px' });
        this.observer.observe(this);
      }
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.playBtn?.removeEventListener('click', this.onToggle);
      this.muteBtn?.removeEventListener('click', this.onMute);
      this.bigPlay?.removeEventListener('click', this.onBigPlay);
    }

    load() {
      if (this.loaded) return;
      const pending = this.video.querySelectorAll('source[data-src]');
      const pendingSelf = this.video.dataset.src;
      this.loaded = true;
      if (!pending.length && !pendingSelf) return;
      pending.forEach((s) => { s.src = s.dataset.src; s.removeAttribute('data-src'); });
      if (pendingSelf) { this.video.src = pendingSelf; this.video.removeAttribute('data-src'); }
      this.video.load();
    }

    play(byUser) {
      if (byUser) {
        this.userPaused = false;
        document.querySelectorAll('e-video').forEach((other) => {
          if (other !== this && !other.autoplay && other.video && !other.video.paused) other.pause(false);
        });
      }
      this.load();
      const p = this.video.play();
      if (p && p.catch) p.catch(() => this.sync());
    }

    pause(byUser) {
      if (byUser) this.userPaused = true;
      this.video.pause();
    }

    setMuted(muted) {
      this.video.muted = muted;
      this.sync();
    }

    sync() {
      const playing = !this.video.paused;
      this.dataset.state = playing ? 'playing' : 'paused';
      const s = theme.strings || {};
      if (this.playBtn) {
        this.playBtn.setAttribute('aria-label', playing ? s.pause : s.play);
        this.playBtn.querySelector('[data-icon-play]')?.toggleAttribute('hidden', playing);
        this.playBtn.querySelector('[data-icon-pause]')?.toggleAttribute('hidden', !playing);
      }
      if (this.muteBtn) {
        this.muteBtn.setAttribute('aria-label', this.video.muted ? s.unmute : s.mute);
        this.muteBtn.querySelector('[data-icon-mute]')?.toggleAttribute('hidden', !this.video.muted);
        this.muteBtn.querySelector('[data-icon-volume]')?.toggleAttribute('hidden', this.video.muted);
      }
    }
  }
  if (!customElements.get('e-video')) customElements.define('e-video', EVideo);

  /* ---------- Before / after comparison ---------- */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('input[type="range"]');
      if (!this.range) return;
      this.onInput = () => this.style.setProperty('--pos', this.range.value + '%');
      /* iOS only drags a range input by its thumb; map taps and horizontal drags anywhere on the image. */
      this.setFromX = (x) => {
        const rect = this.getBoundingClientRect();
        const value = Math.round(Math.min(1, Math.max(0, (x - rect.left) / rect.width)) * 100);
        this.range.value = value;
        this.onInput();
      };
      this.onDown = (e) => { this.dragging = true; this.setFromX(e.clientX); };
      this.onMove = (e) => { if (this.dragging) this.setFromX(e.clientX); };
      this.onUp = () => { this.dragging = false; };
      this.range.addEventListener('input', this.onInput);
      this.range.addEventListener('pointerdown', this.onDown);
      this.range.addEventListener('pointermove', this.onMove);
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => this.range.addEventListener(type, this.onUp));
      this.onInput();
    }
    disconnectedCallback() {
      if (!this.range) return;
      this.range.removeEventListener('input', this.onInput);
      this.range.removeEventListener('pointerdown', this.onDown);
      this.range.removeEventListener('pointermove', this.onMove);
      ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) => this.range.removeEventListener(type, this.onUp));
    }
  }
  if (!customElements.get('before-after')) customElements.define('before-after', BeforeAfter);

  /* ---------- Horizontal scroller with prev/next buttons ---------- */
  class EScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroller-track]');
      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      if (!this.track) return;
      this.onPrev = () => this.go(-1);
      this.onNext = () => this.go(1);
      this.onScroll = theme.debounce(() => this.update(), 60);
      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.onScroll);
    }
    go(dir) {
      const item = this.track.firstElementChild;
      const step = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth * 0.8;
      this.track.scrollBy({ left: dir * step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
    }
  }
  if (!customElements.get('e-scroller')) customElements.define('e-scroller', EScroller);

  /* ---------- Reveal on scroll ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 })
    : null;

  const initReveal = (root = document) => {
    const els = root.querySelectorAll('.reveal:not(.is-visible)');
    if (!revealObserver || (window.Shopify && window.Shopify.designMode)) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    els.forEach((el) => revealObserver.observe(el));
  };
  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* ---------- Theme editor: open drawers for selected blocks ---------- */
  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('cart-drawer')) document.querySelector('cart-drawer')?.open();
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.querySelector('cart-drawer')) document.querySelector('cart-drawer')?.close();
  });
})();
